import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/contexts/CartContext';
import { suppliersAPI } from '@/lib/api';
import { useUnavailableItems } from '@/hooks/useUnavailableItems';
import { toast } from '@/components/ui/sonner';
import { computeDeliveryBreakdown } from '@/lib/delivery';
import { formatPack } from '@/lib/units';
import { money, round2 } from '@/lib/format';

function formatTotalUnit(qty, value, unit) {
  if (!value || !unit) return null;
  const total = qty * value;
  if (unit === 'g' && total >= 1000) return `${(total / 1000).toFixed(1)}kg`;
  if (unit === 'ml' && total >= 1000) return `${(total / 1000).toFixed(1)}L`;
  if (unit === 'mg' && total >= 1000) return `${(total / 1000).toFixed(1)}g`;
  return `${total}${unit}`;
}

export default function CartPage() {
  const { items, updateQuantity, removeItem, keepOnlySupplier, total, itemCount, currentSupplierId, currentSupplierName, multiSupplierAllowed } = useCart();
  const hasMoqViolation = items.some(i => Number(i.moq || 0) > 1 && i.quantity < Number(i.moq));

  // Distinct suppliers in the cart (used to enforce single-supplier ordering).
  const cartSuppliers = useMemo(() => {
    const map = new Map();
    items.forEach(i => {
      const id = i.supplier_id || '';
      if (!map.has(id)) map.set(id, { id, name: i.supplier_name || 'Unknown supplier', count: 0 });
      map.get(id).count += 1;
    });
    return [...map.values()];
  }, [items]);
  const hasMultiSupplierViolation = !multiSupplierAllowed && cartSuppliers.length > 1;
  const { unavailableIds } = useUnavailableItems(items);
  const hasUnavailable = unavailableIds.length > 0;
  const checkoutBlocked = hasMoqViolation || hasMultiSupplierViolation || hasUnavailable;
  const blockReason = hasUnavailable ? 'unavailable' : hasMultiSupplierViolation ? 'multi' : hasMoqViolation ? 'moq' : null;
  const [supplierMetaMap, setSupplierMetaMap] = useState({});

  // Fetch delivery config for every supplier in the cart (multi-vendor safe).
  const supplierIds = useMemo(
    () => [...new Set(items.map(i => i.supplier_id).filter(Boolean))],
    [items]
  );
  const supplierIdsKey = supplierIds.join(',');
  useEffect(() => {
    if (supplierIds.length === 0) { setSupplierMetaMap({}); return undefined; }
    let active = true;
    Promise.all(
      supplierIds.map(id => suppliersAPI.get(id).then(r => [id, r.data]).catch(() => [id, null]))
    ).then(entries => { if (active) setSupplierMetaMap(Object.fromEntries(entries)); });
    return () => { active = false; };
  }, [supplierIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { lines: deliveryLines, total: deliveryCharge } = useMemo(
    () => computeDeliveryBreakdown(items, supplierMetaMap),
    [items, supplierMetaMap]
  );
  const deliveryChargeLines = deliveryLines.filter(l => l.amount > 0);
  const grandTotal = round2(total + deliveryCharge);

  if (items.length === 0) {
    return (
      <div data-testid="cart-page-empty" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
        <ShoppingBag className="h-12 w-12 sm:h-16 sm:w-16 text-foreground/20 mx-auto mb-4 sm:mb-6" />
        <h1 className="font-heading text-2xl sm:text-3xl font-light text-foreground mb-2 sm:mb-3">Your cart is empty</h1>
        <p className="font-body text-xs sm:text-sm text-foreground/50 mb-6 sm:mb-8">Browse our products and add some to your cart.</p>
        <Link to="/shop">
          <Button variant="ctaPrimary" className="rounded-full px-6 sm:px-8 py-5 sm:py-6 font-body text-sm font-bold">
            Continue Shopping <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-6 sm:mb-10">
        <div className="inline-flex items-center gap-3 mb-2 sm:mb-3">
          <div className="h-[2px] w-8 bg-gold rounded-full" />
          <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold font-bold">Your Cart</p>
        </div>
        <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight">
          Shopping Cart
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-10">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {hasMultiSupplierViolation && (
            <div
              data-testid="cart-multi-supplier-warning"
              role="alert"
              className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 sm:p-5"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-bold text-red-300">
                    Your cart has items from {cartSuppliers.length} suppliers
                  </p>
                  <p className="font-body text-xs text-foreground/70 mt-1 leading-relaxed">
                    Orders can only be placed with one supplier at a time. Choose which supplier to keep — the other items will be removed from your cart.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {cartSuppliers.map(s => (
                      <Button
                        key={s.id || 'unknown'}
                        onClick={() => keepOnlySupplier(s.id)}
                        data-testid={`keep-supplier-${s.id || 'unknown'}`}
                        variant="ctaPrimary"
                        className="rounded-full font-body text-xs font-bold px-4 h-9"
                      >
                        Keep only {s.name} ({s.count})
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Table header - desktop only */}
          <div className="hidden sm:flex items-center gap-4 px-5 py-3 bg-muted/50 rounded-xl text-foreground/50">
            <div className="flex-1 font-body text-xs uppercase tracking-wider font-semibold">Product</div>
            <div className="w-[100px] text-right font-body text-xs uppercase tracking-wider font-semibold">Unit Price</div>
            <div className="w-[140px] text-center font-body text-xs uppercase tracking-wider font-semibold">Qty</div>
            <div className="w-[100px] text-right font-body text-xs uppercase tracking-wider font-semibold">Total</div>
            <div className="w-[40px]"></div>
          </div>

          {items.map(item => {
            const totalUnit = formatTotalUnit(item.quantity * (Number(item.pack_qty) || 1), item.unit_size_value, item.unit_size_unit);
            const moq = Number(item.moq || 0);
            const moqShort = moq > 1 && item.quantity < moq ? moq - item.quantity : 0;
            return (
              <div
                key={item.product_id}
                data-testid={`cart-item-${item.product_id}`}
                className="rounded-xl bg-card border border-border"
              >
                {/* Desktop layout */}
                <div className="hidden sm:flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.product_id}`}>
                      <p className="font-body text-base font-semibold text-foreground hover:text-teal transition-colors truncate">
                        {item.name}
                      </p>
                    </Link>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {item.supplier_name && (
                        <Badge variant="outline" className="rounded-full font-body text-[10px] border-gold/30 text-gold">{item.supplier_name}</Badge>
                      )}
                      {item.category_name && (
                        <Badge variant="outline" className="rounded-full font-body text-[10px] border-teal/30 text-teal">{item.category_name}</Badge>
                      )}
                      {formatPack(item) && (
                        <span className="font-body text-xs text-foreground/40">{formatPack(item)}{Number(item.pack_qty || 1) > 1 ? ' per pack' : ' per unit'}</span>
                      )}
                    </div>
                    {totalUnit && (
                      <p className="font-body text-sm font-bold text-teal mt-1" data-testid={`cart-total-unit-${item.product_id}`}>
                        {item.quantity} × {formatPack(item)} = {totalUnit} total
                      </p>
                    )}
                  </div>
                  <div className="w-[100px] text-right flex-shrink-0">
                    <p className="font-body text-sm text-foreground/70">R{money(item.price)}</p>
                  </div>
                  <div className="w-[140px] flex justify-center flex-shrink-0">
                    <div className="flex items-center border border-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="px-2.5 py-2 text-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                        data-testid={`cart-decrease-${item.product_id}`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          if (val > 0) updateQuantity(item.product_id, val);
                        }}
                        className="w-10 text-center font-body text-sm bg-transparent text-foreground border-x border-border py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        data-testid={`cart-qty-input-${item.product_id}`}
                      />
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="px-2.5 py-2 text-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                        data-testid={`cart-increase-${item.product_id}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="w-[100px] text-right flex-shrink-0">
                    <p className="font-body text-base font-bold text-foreground">
                      R{money((item.price * item.quantity))}
                    </p>
                  </div>
                  <div className="w-[40px] flex-shrink-0">
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="p-2 text-foreground/30 hover:text-red-400 transition-colors rounded-lg hover:bg-red-900/20"
                      data-testid={`cart-remove-${item.product_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Mobile layout */}
                <div className="sm:hidden p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-sm font-semibold text-foreground leading-tight">{item.name}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {item.supplier_name && (
                          <Badge variant="outline" className="rounded-full font-body text-[9px] border-gold/30 text-gold px-1.5 py-0">{item.supplier_name}</Badge>
                        )}
                        {item.category_name && (
                          <Badge variant="outline" className="rounded-full font-body text-[9px] border-teal/30 text-teal px-1.5 py-0">{item.category_name}</Badge>
                        )}
                      </div>
                      {totalUnit && (
                        <p className="font-body text-[11px] font-bold text-teal mt-1">
                          {item.quantity} × {formatPack(item)} = {totalUnit}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="p-1.5 text-foreground/30 hover:text-red-400 transition-colors rounded-lg"
                      data-testid={`cart-remove-mobile-${item.product_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center border border-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="px-2 py-1.5 text-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          if (val > 0) updateQuantity(item.product_id, val);
                        }}
                        className="w-8 text-center font-body text-xs bg-transparent text-foreground border-x border-border py-1.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="px-2 py-1.5 text-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-body text-xs text-foreground/50">R{money(item.price)} ea</p>
                      <p className="font-body text-sm font-bold text-foreground">R{money((item.price * item.quantity))}</p>
                    </div>
                  </div>
                </div>

                {unavailableIds.includes(item.product_id) && (
                  <div className="px-5 pb-3 sm:pt-0 -mt-1" data-testid={`cart-unavailable-warning-${item.product_id}`}>
                    <div className="flex items-center gap-2 rounded-lg bg-destructive/10 border border-destructive/40 px-3 py-2">
                      <AlertCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                      <p className="font-body text-xs text-destructive">
                        No longer available — remove to check out.
                      </p>
                    </div>
                  </div>
                )}

                {moqShort > 0 && (
                  <div
                    className="px-5 pb-3 sm:pt-0 -mt-1"
                    data-testid={`cart-moq-warning-${item.product_id}`}
                  >
                    <div className="flex items-center gap-2 rounded-lg bg-red-900/15 border border-red-700/30 px-3 py-2">
                      <AlertCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
                      <p className="font-body text-xs text-red-400">
                        Minimum order is <strong>{moq} units</strong> — add <strong>{moqShort}</strong> more to checkout.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="cart-summary">
            <h3 className="font-heading text-lg sm:text-xl font-medium text-foreground mb-4 sm:mb-6">Order Summary</h3>
            {currentSupplierName && (
              <div className="mb-4 p-2.5 rounded-lg bg-gold/10 border border-gold/25 flex items-center gap-2" data-testid="cart-supplier-badge">
                <Truck className="h-3.5 w-3.5 text-gold flex-shrink-0" strokeWidth={2} />
                <p className="font-body text-xs text-foreground/80 truncate">
                  Supplier: <span className="text-gold font-semibold">{currentSupplierName}</span>
                </p>
              </div>
            )}
            <div className="space-y-3 font-body text-sm">
              <div className="flex justify-between text-foreground/60">
                <span>Items ({itemCount})</span>
                <span>R{money(total)}</span>
              </div>
              {deliveryChargeLines.length > 1 ? (
                <div className="space-y-1" data-testid="cart-delivery-line">
                  <div className="flex justify-between text-foreground/60">
                    <span>Delivery</span>
                    <span>R{money(deliveryCharge)}</span>
                  </div>
                  {deliveryChargeLines.map(l => (
                    <div key={l.supplier_id} className="flex justify-between text-foreground/40 text-xs pl-3" data-testid={`cart-delivery-sub-${l.supplier_id}`}>
                      <span className="truncate mr-2">{l.name}{l.type === 'percentage' ? ` (${l.value}%)` : ''}</span>
                      <span>R{money(l.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : deliveryCharge > 0 && (
                <div className="flex justify-between text-foreground/60" data-testid="cart-delivery-line">
                  <span>
                    Delivery
                    {deliveryChargeLines[0]?.type === 'percentage' && (
                      <span className="text-foreground/40"> ({deliveryChargeLines[0].value}%)</span>
                    )}
                  </span>
                  <span>R{money(deliveryCharge)}</span>
                </div>
              )}
              <div className="flex justify-between text-foreground/60">
                <span>Fulfillment</span>
                <span className="text-teal font-semibold">Collection</span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between items-center text-foreground font-bold text-base sm:text-lg">
                <span>Total</span>
                <span className="text-gold" data-testid="cart-grand-total">R{money(grandTotal)}</span>
              </div>
            </div>
            {checkoutBlocked ? (
              <div className="block mt-6 sm:mt-8">
                <Button
                  disabled
                  data-testid="proceed-checkout-btn"
                  variant="ctaPrimary"
                  className="w-full rounded-xl py-5 sm:py-6 font-body text-sm font-bold"
                >
                  {blockReason === 'unavailable'
                    ? 'Remove unavailable items to continue'
                    : blockReason === 'multi'
                      ? 'Select a single supplier to continue'
                      : 'Meet minimum quantities to continue'}
                </Button>
                {blockReason === 'unavailable' ? (
                  <div className="mt-2 flex flex-col items-center gap-2">
                    <p className="font-body text-xs text-red-400 text-center">
                      Some items are no longer available (see above).
                    </p>
                    <Button
                      type="button"
                      variant="ctaDanger"
                      size="sm"
                      data-testid="cart-remove-unavailable-btn"
                      onClick={() => {
                        const clearingAll = unavailableIds.length >= items.length;
                        unavailableIds.forEach(id => removeItem(id));
                        if (clearingAll) toast.error('Cart cleared — none of those items are available anymore');
                      }}
                      className="rounded-full font-body text-xs"
                    >
                      Remove unavailable item{unavailableIds.length > 1 ? 's' : ''}
                    </Button>
                  </div>
                ) : (
                  <p className="font-body text-xs text-red-400 text-center mt-2">
                    {blockReason === 'multi'
                      ? 'Your cart has items from multiple suppliers (see above).'
                      : 'Some items are below their minimum order quantity (see above).'}
                  </p>
                )}
              </div>
            ) : (
              <Link to="/checkout" className="block mt-6 sm:mt-8">
                <Button
                  data-testid="proceed-checkout-btn"
                  variant="ctaPrimary"
                  className="w-full rounded-xl py-5 sm:py-6 font-body text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-lg"
                >
                  Proceed to Checkout <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
            <Link to="/shop" className="block mt-3 text-center">
              <Button variant="ghost" className="font-body text-sm text-foreground/50 hover:text-teal">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
